import * as Sentry from '@sentry/react';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

import { useScreenHeight } from '../hooks/use-screen-height';
import { Button } from '../ui/button';

export function ErrorBoundary({ children }: { children?: React.ReactNode }) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <SentryErrorBoundary reset={reset}>
          <ReactErrorBoundary
            onReset={reset}
            fallbackRender={({ resetErrorBoundary }) => (
              <CenteredTryAgainError resetErrorBoundary={resetErrorBoundary} />
            )}
          >
            {children}
          </ReactErrorBoundary>
        </SentryErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}

function SentryErrorBoundary({
  children,
  reset,
}: {
  children: React.ReactNode;
  reset: () => void;
}) {
  const ErrorBoundaryWithSentry = Sentry.withErrorBoundary(
    ({ children }: { children: React.ReactNode }) => <>{children}</>,
    {
      fallback: (props) => (
        <CenteredTryAgainError {...props} resetErrorBoundary={reset} />
      ),
    },
  );

  return <ErrorBoundaryWithSentry>{children}</ErrorBoundaryWithSentry>;
}

function CenteredTryAgainError({
  error,
  resetErrorBoundary,
}: {
  error?: unknown;
  resetErrorBoundary: () => void;
}) {
  const height = useScreenHeight();

  return (
    <div
      className={`w-screen ${height} flex flex-col justify-center items-center`}
    >
      <h1 className="pb-4 text-xl font-bold">Oops!</h1>
      {error instanceof Error && (
        <p className="text-sm text-gray-500">{error.message}</p>
      )}
      <Button
        variant="primary"
        onClick={() => {
          resetErrorBoundary();
        }}
      >
        Tentar novamente
      </Button>
    </div>
  );
}
